import React, { useState, useEffect, act } from 'react'
import { ArrowLeftIcon, ArrowRightIcon, InformationCircleIcon, ClockIcon, ArrowDownTrayIcon, EnvelopeIcon } from '@heroicons/react/20/solid'
import { RoutesIcon } from '../../../components/icons'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { t } from 'i18next'
import loadingImage from 'assets/images/boat-loading.gif'
import { GET_CAPTAIN_ROUTE_BOAT_HISTORY_WAYPOINTS_LIST } from 'graphql/operations/queries'
import {
    ACTIVATE_BOAT_ROUTE_TRIP,
    CANCEL_BOAT_ROUTE_TRIP,
    UPDATE_ROUTE_TRIP_WAYPOINT_STATUS,
    REMOVE_BOAT_ROUTE_ADDITIONAL_CAPTAIN, // Add this import
    TRIGGER_RECIPIENT_EMAIL,
    DOWNLOAD_PASSENGER_MANIFEST,
} from 'graphql/operations/mutations'
import { Badge } from 'components/badge'
import { AssignBoatDialog, AssignLuggageBoatDialog, AssignCaptainDialog, AssignAdditionalCaptainDialog } from '../assignDialog'
import { SuccessDialog, ErrorDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { SeatManagementModal } from 'components/custom/SeatManagementModal'
import { UserListDropdown } from 'components/custom/userListDropdown'
import { ADD_BOAT_ROUTE_ADDITIONAL_CAPTAIN } from 'graphql/boat/mutations'
import { customDateFormat, customTimeFormat, utcDateTimeToBahrainTime } from 'utils/common'
import noTripFound from 'assets/images/icons/no-trip-found.svg'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { SEARCH_MEAL_SUMMARY } from 'graphql/meals/queries'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function RouteBoatDetails() {
    const { id } = useParams()
    const location = useLocation()
    const activity_date = location.state?.activity_date || new Date().toISOString().split('T')[0]
    const navigate = useNavigate()
    const [historyInfo, setHistoryInfo] = useState(null)
    const [selectedHistory, setSelectedHistory] = useState(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [showBoatAssignDialog, setShowBoatAssignDialog] = useState(false)
    const [showBoatLuggageAssignDialog, setShowBoatLuggageAssignDialog] = useState(false)
    const [showCaptainAssignDialog, setShowCaptainAssignDialog] = useState(false)
    const [showCaptain2AssignDialog, setShowCaptain2AssignDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('')
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('')
    const [confirmDialogAction, setConfirmDialogAction] = useState(null)
    const [mealSummaryData, setMealSummaryData] = useState([])
    const [mealSummaryLoading, setMealSummaryLoading] = useState(false)
    const [passengerStatus, setPassengerStatus] = useState('')
    const [downloadManifestLoading, setDownloadManifestLoading] = useState(false)
    const [downloadAttachmentsLoading, setDownloadAttachmentsLoading] = useState(false)
    const [showSeatManagementModal, setShowSeatManagementModal] = useState(false)
    
    // Seaman assignment states
    const [isAddingSeaman1, setIsAddingSeaman1] = useState(false)
    const [isAddingSeaman2, setIsAddingSeaman2] = useState(false)
    const [selectedSeaman1Id, setSelectedSeaman1Id] = useState('')
    const [selectedSeaman2Id, setSelectedSeaman2Id] = useState('')
    const [seamanAssignLoading, setSeamanAssignLoading] = useState(false)

    // Get Boat List
    const [getAssignmentHistoryDetails, { loading: historyLoading, error: historyListError }] = useLazyQuery(
        GET_CAPTAIN_ROUTE_BOAT_HISTORY_WAYPOINTS_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getCaptainRouteBoatHistory.success && data.getCaptainRouteBoatHistory.code === '200') {
                    const historyData = data.getCaptainRouteBoatHistory.history[0]
                    console.log('History Data for manifest: ', {
                        history_id: historyData?.history_id,
                        route_id: historyData?.route?.route_id,
                        activity_date: historyData?.activity_date
                    })
                    // Ensure route_id is properly mapped
                    const mappedHistoryData = {
                        ...historyData,
                        route: {
                            ...historyData.route,
                            id: historyData.route?.route_id // Map route_id to id
                        }
                    }
                    setHistoryInfo(mappedHistoryData)
                }
            },
        }
    )

    // Trigger recipient email (Passenger Manifest)
    const [triggerRecipientEmail, { loading: emailLoading }] = useMutation(TRIGGER_RECIPIENT_EMAIL, {
        onCompleted: data => {
            if (data?.triggerRecipientEmail?.code === '200' && data?.triggerRecipientEmail?.success === true) {
                setShowSucDialog(true)
                setSuccessMessage(data?.triggerRecipientEmail?.message || t('Email sent successfully'))
                setTimeout(() => {
                    setShowSucDialog(false)
                    setSuccessMessage('')
                }, 1500)
            } else {
                setShowErrDialog(true)
                setSuccessMessage(
                    data?.triggerRecipientEmail?.message || t('Failed to send passenger manifest. Please try again.')
                )
            }
        },
        onError: error => {
            setShowErrDialog(true)
            setSuccessMessage(error?.message || t('An error occurred while sending passenger manifest'))
        },
    })

    // Download passenger manifest mutation
    const [downloadPassengerManifest] = useMutation(DOWNLOAD_PASSENGER_MANIFEST, {
        onCompleted: (data, { variables }) => {            
            if (variables.type === 'PASSENEGR_RECORD') {
                setDownloadManifestLoading(false)
            } else {
                setDownloadAttachmentsLoading(false)
            }
            
            if (data?.downloadPassengerManifest?.code === '200' && data?.downloadPassengerManifest?.success === true) {
                if (data?.downloadPassengerManifest?.url) {
                    // Open the download URL in a new tab
                    window.open(data?.downloadPassengerManifest?.url, '_blank')
                    setShowSucDialog(true)
                    setSuccessMessage(data?.downloadPassengerManifest?.message || t('Download started successfully'))
                    setTimeout(() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }, 1500)
                } else {
                    setShowErrDialog(true)
                    setSuccessMessage(t('Download URL not available'))
                }
            } else {
                setShowErrDialog(true)
                setSuccessMessage(
                    data?.downloadPassengerManifest?.message || t('Failed to download. Please try again.')
                )
            }
        },
        onError: (error, { variables }) => {
            // Clear the appropriate loading state based on the type
            if (variables.type === 'PASSENEGR_RECORD') {
                setDownloadManifestLoading(false)
            } else {
                setDownloadAttachmentsLoading(false)
            }
            setShowErrDialog(true)
            setSuccessMessage(error?.message || t('An error occurred while downloading'))
        },
    })

    const handleSendPassengerManifest = () => {
        if (!historyInfo?.history_id || !historyInfo?.route?.route_id || !historyInfo?.activity_date) {
            console.log('Missing data:', {
                history_id: historyInfo?.history_id,
                route_id: historyInfo?.route?.route_id,
                activity_date: historyInfo?.activity_date
            })
            setShowErrDialog(true)
            setSuccessMessage(t('Missing trip details to send passenger manifest'))
            return
        }
        if (!passengerStatus) {
            setShowErrDialog(true)
            setSuccessMessage(t('Please select passenger status'))
            return
        }
        triggerRecipientEmail({
            variables: {
                historyId: historyInfo.history_id,
                routeId: historyInfo.route.route_id,
                tripDate: historyInfo.activity_date,
                passengerStatus: passengerStatus,
            },
        })
    }

    const handleDownloadPassengerManifest = () => {
        if (!historyInfo?.history_id || !historyInfo?.route?.route_id || !historyInfo?.activity_date) {
            setShowErrDialog(true)
            setSuccessMessage(t('Missing trip details to download passenger manifest'))
            return
        }
        if (!passengerStatus) {
            setShowErrDialog(true)
            setSuccessMessage(t('Please select passenger status'))
            return
        }
        setDownloadManifestLoading(true)
        downloadPassengerManifest({
            variables: {
                type: 'PASSENEGR_RECORD',
                routeId: historyInfo.route.route_id,
                tripDate: historyInfo.activity_date,
                passengerStatus: passengerStatus,
                historyId: historyInfo.history_id,
            },
        })
    }

    const handleDownloadPassengerAttachments = () => {
        if (!historyInfo?.history_id || !historyInfo?.route?.route_id || !historyInfo?.activity_date) {
            setShowErrDialog(true)
            setSuccessMessage(t('Missing trip details to download passenger attachments'))
            return
        }
        if (!passengerStatus) {
            setShowErrDialog(true)
            setSuccessMessage(t('Please select passenger status'))
            return
        }
        setDownloadAttachmentsLoading(true)
        downloadPassengerManifest({
            variables: {
                type: 'PASSENEGR_ATTACHMENTS',
                routeId: historyInfo.route.route_id,
                tripDate: historyInfo.activity_date,
                passengerStatus: passengerStatus,
                historyId: historyInfo.history_id,
            },
        })
    }

    const [updateWaypointStatus, { loading: waypointLoading }] = useMutation(UPDATE_ROUTE_TRIP_WAYPOINT_STATUS, {
        onCompleted: data => {
            if (
                data?.updateRouteTripWaypointStatus.code === '200' &&
                data?.updateRouteTripWaypointStatus.success === true
            ) {
                console.log('Waypoint status updated successfully')
                setShowSucDialog(true)
                setSuccessMessage(data?.updateRouteTripWaypointStatus.message)
                searchAssignmentHistory()
                setTimeout(() => {
                    setShowSucDialog(false)
                }, 3000)
            } else {
                setSuccessMessage(data?.sendBookingTicket.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSuccessMessage(t('Something went wrong, please try again later'))
            setShowErrDialog(true)
        },
    })

    const [removeAdditionalCaptain, { loading: removeCaptainLoading }] = useMutation(
        REMOVE_BOAT_ROUTE_ADDITIONAL_CAPTAIN,
        {
            onCompleted: data => {
                if (
                    data?.removeBoatRouteAdditionalCaptain.code === '200' &&
                    data?.removeBoatRouteAdditionalCaptain.success === true
                ) {
                    setShowSucDialog(true)
                    setSuccessMessage(data?.removeBoatRouteAdditionalCaptain.message)
                    // Refresh data to show updated state
                    searchAssignmentHistory()
                    setTimeout(() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }, 1500)
                } else {
                    setShowErrDialog(true)
                    setSuccessMessage(data?.removeBoatRouteAdditionalCaptain?.message || t('Something went wrong, please try again later'))
                }
            },
            onError: error => {
                setShowErrDialog(true)
                setSuccessMessage(error?.message || t('Something went wrong, please try again later'))
            },
        }
    )

    // Assign Seaman Mutation
    const [assignSeaman] = useMutation(ADD_BOAT_ROUTE_ADDITIONAL_CAPTAIN, {
        onCompleted: data => {
            setSeamanAssignLoading(false)
            if (data?.addBoatRouteAdditionalCaptain?.code === '200' && data?.addBoatRouteAdditionalCaptain?.success) {
                setShowSucDialog(true)
                setSuccessMessage(data?.addBoatRouteAdditionalCaptain?.message || t('Seaman assigned successfully'))
                setIsAddingSeaman1(false)
                setIsAddingSeaman2(false)
                setSelectedSeaman1Id('')
                setSelectedSeaman2Id('')
                searchAssignmentHistory()
                setTimeout(() => {
                    setShowSucDialog(false)
                    setSuccessMessage('')
                }, 1500)
            } else {
                setShowErrDialog(true)
                setSuccessMessage(data?.addBoatRouteAdditionalCaptain?.message || t('Something went wrong, please try again later'))
            }
        },
        onError: error => {
            setSeamanAssignLoading(false)
            setShowErrDialog(true)
            setSuccessMessage(error?.message || t('Something went wrong, please try again later'))
        },
    })

    const [getMealSummary, { loading: mealLoading, error: mealError }] = useLazyQuery(SEARCH_MEAL_SUMMARY, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchMealSummary.success && data.searchMealSummary.code === '200') {
                setMealSummaryData(data.searchMealSummary.trip_meals_summary)
            }
        },
        onError: error => {
            console.error('Error fetching meal summary:', error)
            setMealSummaryData([])
        },
    })

    const fetchMealSummary = () => {
        if (historyInfo?.history_id && historyInfo?.activity_date) {
            getMealSummary({
                variables: {
                    tripId: historyInfo.history_id,
                    toDate: historyInfo.activity_date,
                    fromDate: historyInfo.activity_date,
                    mealId: null,
                },
            })
        }
    }

    useEffect(() => {
        if (historyInfo?.history_id && historyInfo?.activity_date) {
            fetchMealSummary()
        }
    }, [historyInfo])

    // Search boats Boat List
    const searchAssignmentHistory = () => {
        console.log('Search Assignment History')
        getAssignmentHistoryDetails({
            variables: {
                historyId: id,
                captainId: null,
                routeId: null,
                boatId: null,
                activityDate: activity_date,
            },
        })
    }

    useEffect(() => {
        searchAssignmentHistory()
    }, [id])

    const handleBoatCaptainChange = action => {
        const params = {
            historyId: id,
            date: historyInfo?.activity_date,
            captainId: historyInfo?.captain?.id,
            captainName: `${historyInfo.captain?.user_info?.given_name || ''} ${
                historyInfo.captain?.user_info?.family_name || ''
            }`.trim(),
            captain2Id: historyInfo?.captain2?.id,
            captain2Name: `${historyInfo.captain2?.user_info?.given_name || ''} ${
                historyInfo.captain2?.user_info?.family_name || ''
            }`.trim(),
            boatName: historyInfo?.boat?.name.en,
            boatId: historyInfo?.boat?.id,
            startTime: historyInfo?.start_time,
            endTime: historyInfo?.end_time,
            // Include seaman IDs to preserve them during captain updates
            seamanOneId: historyInfo?.seaman_id1 || null,
            seamanTwoId: historyInfo?.seaman_id2 || null,
        }
        setSelectedHistory(params)
        if (action === 'BOAT') {
            setShowBoatAssignDialog(true)
        } else if (action === 'CAPTAIN') {
            setShowCaptainAssignDialog(true)
        } else if (action === 'CAPTAIN2') {
            setShowCaptain2AssignDialog(true)
        } else {
            console.log('Change Boat & Captain')
        }
    }

    const handleLuggageBoatChange = () => {
        const params = {
            historyId: id,
            date: historyInfo?.activity_date,
            captainId: historyInfo?.captain?.id,
            captainName: `${historyInfo.captain?.user_info?.given_name || ''} ${
                historyInfo.captain?.user_info?.family_name || ''
            }`.trim(),
            captain2Id: historyInfo?.captain2?.id,
            captain2Name: `${historyInfo.captain2?.user_info?.given_name || ''} ${
                historyInfo.captain2?.user_info?.family_name || ''
            }`.trim(),
            boatName: historyInfo?.boat?.name.en,
            boatId: historyInfo?.boat?.id,
            luggage_boats: historyInfo?.luggage_boats,
            startTime: historyInfo?.start_time,
            endTime: historyInfo?.end_time,
            // Include seaman IDs to preserve them during captain updates
            seamanOneId: historyInfo?.seaman_id1 || null,
            seamanTwoId: historyInfo?.seaman_id2 || null,
        }
        setSelectedHistory(params)
        setShowBoatLuggageAssignDialog(true)
    }

    const handleCloseDialog = () => {
        setShowBoatAssignDialog(false)
        setShowBoatLuggageAssignDialog(false);
        setShowCaptainAssignDialog(false)
        setShowCaptain2AssignDialog(false)
        setSelectedHistory(null)
    }
    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
        setShowErrDialog('')
    }

    const handleBack = () => {
        navigate('/route-operations')
    }

    // Seaman Assignment Handlers
    const handleSeaman1Add = () => {
        if (!selectedSeaman1Id) return
        setSeamanAssignLoading(true)
        assignSeaman({
            variables: {
                historyId: id,
                captainId: historyInfo?.captain2?.id || historyInfo?.captain_id2 || null,
                activityDate: historyInfo?.activity_date,
                seamanOneId: selectedSeaman1Id,
                seamanTwoId: historyInfo?.seaman_id2 || null,
            },
        })
    }

    const handleSeaman2Add = () => {
        if (!selectedSeaman2Id) return
        setSeamanAssignLoading(true)
        assignSeaman({
            variables: {
                historyId: id,
                captainId: historyInfo?.captain2?.id || historyInfo?.captain_id2 || null,
                activityDate: historyInfo?.activity_date,
                seamanOneId: historyInfo?.seaman_id1 || null,
                seamanTwoId: selectedSeaman2Id,
            },
        })
    }

    const handleSeaman1Cancel = () => {
        setIsAddingSeaman1(false)
        setSelectedSeaman1Id('')
    }

    const handleSeaman2Cancel = () => {
        setIsAddingSeaman2(false)
        setSelectedSeaman2Id('')
    }

    const handleSeaman1Change = () => {
        setIsAddingSeaman1(true)
        setSelectedSeaman1Id(historyInfo?.seaman_id1 || '')
    }

    const handleSeaman2Change = () => {
        setIsAddingSeaman2(true)
        setSelectedSeaman2Id(historyInfo?.seaman_id2 || '')
    }

    const handleWaypointStatus = (status, type) => {
        let title = type == 'open' ? 'Open Station' : 'Close Station'
        let description =
            type === 'open'
                ? 'Are you sure you want to Open the station?'
                : 'Are you sure you want to Close the station?'
        setConfirmDialogTitle(t(title))
        setConfirmDialogMessage(t(description))
        setConfirmDialogAction(() => () => {
            openCloseStation(status)
        })
        setShowConfirmDialog(true)
    }

    const openCloseStation = waypoint => {
        const tripLocation = {
            history_id: historyInfo.history_id,
            trip_date: historyInfo.activity_date,
            status_code: waypoint.status.code === 'OPN' ? 'CLD' : 'OPN',
            location_id: waypoint.location.id,
        }
        updateWaypointStatus({
            variables: { tripLocation },
        })
    }

    // Convert time to AM/PM format
    const formatTimeToAmPm = time => {
        const [hours, minutes, seconds] = time.split(':')
        const date = new Date()
        date.setHours(hours, minutes, seconds)
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        })
    }

    // Trip Cancel
    const [cancelTrip, { loading: updateLoading }] = useMutation(CANCEL_BOAT_ROUTE_TRIP, {
        onCompleted: data => {
            if (data?.cancelBoatRouteTrip.code === '200' && data?.cancelBoatRouteTrip.success === true) {
                setShowSucDialog(true)
                setSuccessMessage(data?.cancelBoatRouteTrip.message)
                searchAssignmentHistory()
                setTimeout(() => {
                    setShowSucDialog(false)
                    setSuccessMessage('')
                }, 1500)
            } else {
                setShowConfirmDialog(false)
                setShowErrDialog(true)
                setSuccessMessage(data?.cancelBoatRouteTrip.message)
            }
        },
        onError: error => {
            setShowErrDialog(true)
            setSuccessMessage(error.message)
        },
    })
    const cancelBoatTrip = params => {
        setSelectedHistory(params)
        setConfirmDialogTitle(t('Cancel Trip'))
        setConfirmDialogMessage(t('Are you sure you want to cancel this trip?'))
        setConfirmDialogAction(() => () => {
            cancelTrip({ variables: { historyId: params.history_id, tripDate: params.activity_date } })
        })
        setShowConfirmDialog(true)
    }
    const [activateTrip, { loading: updateLoading1 }] = useMutation(ACTIVATE_BOAT_ROUTE_TRIP, {
        onCompleted: data => {
            if (data?.activateBoatRouteTrip.code === '200' && data?.activateBoatRouteTrip.success === true) {
                setShowSucDialog(true)
                setSuccessMessage(data?.activateBoatRouteTrip.message)
                searchAssignmentHistory()
                setTimeout(() => {
                    setShowSucDialog(false)
                    setSuccessMessage('')
                }, 1500)
            } else {
                setShowConfirmDialog(false)
                setShowErrDialog(true)
                setSuccessMessage(data?.activateBoatRouteTrip.message)
            }
        },
        onError: error => {
            setShowErrDialog(true)
            setSuccessMessage(error.message)
        },
    })
    const activateBoatTrip = params => {
        console.log('Activate Trip: ', params)
        setSelectedHistory(params)
        setConfirmDialogTitle(t('Activate Trip'))
        setConfirmDialogMessage(t('Are you sure you want to activate this trip?'))
        setConfirmDialogAction(() => () => {
            activateTrip({ variables: { historyId: params.history_id, tripDate: params.activity_date } })
        })
        setShowConfirmDialog(true)
    }

    const removeCaptain = params => {
        setSelectedHistory(params)
        setConfirmDialogTitle(t('Remove Additional Captain'))
        setConfirmDialogMessage(t('Are you sure you want to remove this additional captain? Note: Seamen assignments will be preserved.'))
        setConfirmDialogAction(() => () => {
            removeAdditionalCaptain({ 
                variables: { 
                    historyId: params.history_id, 
                    activityDate: params.activity_date 
                } 
            }).then(() => {
                // Refresh the page data after successful removal
                searchAssignmentHistory()
            })
        })
        setShowConfirmDialog(true)
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <div className="flex">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1">{t('Trip details')}</h3>
                </div>
                <div className="flex gap-2">
                    {historyInfo?.status?.code === 'SCH' || historyInfo?.status?.code === 'ACT' ? (
                        <>
                            <button
                                onClick={() => setShowSeatManagementModal(true)}
                                type="button"
                                className="rounded-md bg-secondary px-3 py-2 text-[14px] text-white shadow-sm font-semibold hover:bg-secondary/[0.9]"
                            >
                                {t('Manage Seats')}
                            </button>
                            <button
                                onClick={() => cancelBoatTrip(historyInfo)}
                                type="button"
                                className="rounded-md bg-primary px-3 py-2 text-[14px] text-white shadow-sm font-semibold hover:bg-primary/[0.9]"
                            >
                                {t('Cancel Trip')}
                            </button>
                        </>
                    ) : null}
                    {historyInfo?.status?.code === 'CAN' ? (
                        <button
                            onClick={() => activateBoatTrip(historyInfo)}
                            type="button"
                            className="rounded-md bg-primary px-3 py-2 text-[14px] text-white shadow-sm font-semibold hover:bg-primary/[0.9]"
                        >
                            {t('Activate Trip')}
                        </button>
                    ) : null}
                </div>
            </div>
            {(historyLoading || waypointLoading) && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}
            {historyListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch operations!')}</div>
                </div>
            )}

            {!historyLoading && !waypointLoading && !historyListError && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="mt-4">
                            <div className="md:flex md:items-center md:space-x-5">
                                <div className="flex items-start space-x-5">
                                    <div className="flex-shrink-0 mb-4">
                                        <div className="relative">
                                            <div className="w-12 h-12 bg-gray-50 p-4 flex justify-center items-center">
                                                <RoutesIcon className="w-6 h-6 fill-primary group-hover:fill-white" />
                                            </div>
                                            <span
                                                aria-hidden="true"
                                                className="absolute inset-0 rounded-lg shadow-inner"
                                            />
                                        </div>
                                    </div>
                                    <div className="pt-1">
                                        <p className="text-sm font-medium text-gray-500">{t('Route Name')}</p>
                                        <h1 className="text-md font-bold text-gray-900">
                                            {historyInfo?.route?.route_name?.en || ''}
                                        </h1>
                                    </div>
                                </div>
                                <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full bg-white text-primary">
                                    <ArrowRightIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                                </span>
                                <div className="flex items-start space-x-5">
                                    <div className="flex-shrink-0 mb-4">
                                        <div className="relative">
                                            <div className="w-12 h-12 bg-gray-50 p-4 flex justify-center items-center">
                                                <svg
                                                    height="100%"
                                                    viewBox="0 0 512 512"
                                                    width="100%"
                                                    className="fill-primary"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    id="fi_3710370"
                                                >
                                                    <g id="Navigator">
                                                        <path d="m456.59 74.3682a86.939 86.939 0 0 0 -132.2211 112.0053l56.8331 79.5625a17.1 17.1 0 0 0 27.832 0l56.833-79.5625a86.6665 86.6665 0 0 0 -9.277-112.0053zm-61.472 89.9763a28.5 28.5 0 1 1 28.5-28.5 28.5014 28.5014 0 0 1 -28.5 28.5z"></path>
                                                        <path d="m395.118 314.9019a17.0965 17.0965 0 0 0 -17.098 17.0981v36.46a60.4419 60.4419 0 1 1 -120.8838 0v-155.08a94.6382 94.6382 0 1 0 -189.2763 0v80.62a17.0982 17.0982 0 0 0 34.1963 0v-80.62a60.4419 60.4419 0 1 1 120.8837 0v155.08a94.6382 94.6382 0 0 0 189.2764 0v-36.46a17.0965 17.0965 0 0 0 -17.0983-17.0981z"></path>
                                                        <path d="m84.9578 352.9019a55.0981 55.0981 0 1 0 55.0982 55.0981 55.1578 55.1578 0 0 0 -55.0982-55.0981z"></path>
                                                    </g>
                                                </svg>
                                            </div>
                                            <span
                                                aria-hidden="true"
                                                className="absolute inset-0 rounded-lg shadow-inner"
                                            />
                                        </div>
                                    </div>
                                    <div className="pt-1">
                                        <p className="text-sm font-medium text-gray-500">{t('Trip Name')}</p>
                                        <h1 className="text-md font-bold text-gray-900">
                                            {historyInfo?.trip?.trip_name?.en || ''}
                                        </h1>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 xl:grid-cols-5 gap-y-6 xl:gap-x-6">
                                <div className="col-span-3">
                                    <div className="mt-3 border-t border-gray-100">
                                        <dl className="divide-y divide-gray-100">
                                            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {t('Date')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {customDateFormat(historyInfo?.activity_date)}
                                                </dd>
                                            </div>
                                            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {t('Start Time')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {utcDateTimeToBahrainTime(historyInfo?.start_time)}
                                                </dd>
                                            </div>
                                            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {t('End Time')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {utcDateTimeToBahrainTime(historyInfo?.end_time)}
                                                </dd>
                                            </div>
                                            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    Route Name
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {historyInfo?.route?.route_name?.en || ''}
                                                </dd>
                                            </div>
                                            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    Trip Name
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {historyInfo?.route?.route_name?.en || ''}
                                                </dd>
                                            </div>
                                            {historyInfo?.is_trip_private && (
                                                <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Private Trip')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        <Badge color="rose">{t('Yes')}</Badge>
                                                    </dd>
                                                </div>
                                            )}
                                            {(historyInfo?.status?.code === 'ACT' ||
                                                historyInfo?.status?.code === 'SCH') && (
                                                <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        Boat
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {historyInfo?.boat?.name.en || '' || (
                                                            <span className="italic text-gray-500">
                                                                {t('No Boat Assigned')}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => {
                                                                handleBoatCaptainChange('BOAT')
                                                            }}
                                                            type="button"
                                                            className="rounded-md bg-primary px-2 py-1 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2"
                                                        >
                                                            {t('Change Boat')}
                                                        </button>
                                                    </dd>
                                                </div>
                                            )}
                                            {(historyInfo?.status?.code === 'ACT' ||
                                                historyInfo?.status?.code === 'SCH') && (
                                                <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Captain')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {historyInfo?.captain?.user_info?.given_name || ''}{' '}
                                                        {historyInfo?.captain?.user_info?.family_name || '' || (
                                                            <span className="italic text-gray-500">
                                                                {t('No Captain Assigned')}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => {
                                                                handleBoatCaptainChange('CAPTAIN')
                                                            }}
                                                            type="button"
                                                            className="rounded-md bg-primary px-2 py-1 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2"
                                                        >
                                                            {t('Change Captain')}
                                                        </button>
                                                    </dd>
                                                </div>
                                            )}
                                            {(historyInfo?.status?.code === 'ACT' ||
                                                historyInfo?.status?.code === 'SCH') &&
                                            historyInfo?.captain_id ? (
                                                <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Additional Captain')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {historyInfo?.captain2?.user_info?.given_name || ''}{' '}
                                                        {historyInfo?.captain2?.user_info?.family_name || '' || (
                                                            <>
                                                                <span className="italic text-gray-500">
                                                                    {t('No Captain Assigned')}
                                                                </span>
                                                                <button
                                                                    onClick={() => {
                                                                        handleBoatCaptainChange('CAPTAIN2')
                                                                    }}
                                                                    type="button"
                                                                    className="rounded-md bg-primary px-2 py-1 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2"
                                                                >
                                                                    {t('Add Captain')}
                                                                </button>
                                                            </>
                                                        )}
                                                        {historyInfo?.captain2?.id && (
                                                            <button
                                                                onClick={() => {
                                                                    removeCaptain(historyInfo)
                                                                }}
                                                                type="button"
                                                                className="text-white rounded-md bg-red px-2 py-1 text-[14px]shadow-sm ms-2"
                                                            >
                                                                {t('Remove Captain')}
                                                            </button>
                                                        )}
                                                    </dd>
                                                </div>
                                            ) : null}
                                            {/* Seaman 1 Section */}
                                            {(historyInfo?.status?.code === 'ACT' || historyInfo?.status?.code === 'SCH') && (
                                                <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Seaman 1')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {!isAddingSeaman1 ? (
                                                            <>
                                                                {historyInfo?.seaman1?.user_info?.given_name || ''}{' '}
                                                                {historyInfo?.seaman1?.user_info?.family_name || ''}
                                                                {!historyInfo?.seaman1?.id && (
                                                                    <span className="italic text-gray-500">
                                                                        {t('No Seaman 1 Assigned')}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    onClick={handleSeaman1Change}
                                                                    type="button"
                                                                    disabled={seamanAssignLoading}
                                                                    className="rounded-md bg-primary px-2 py-1 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2 disabled:opacity-50"
                                                                >
                                                                    {historyInfo?.seaman1?.id ? t('Change') : t('Add')}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <UserListDropdown
                                                                    userType="seaman"
                                                                    fields={{
                                                                        name: 'seaman1Id',
                                                                        id: 'seaman1Id',
                                                                        value: selectedSeaman1Id,
                                                                        onChange: e => setSelectedSeaman1Id(e.target.value),
                                                                    }}
                                                                    purpose="add"
                                                                    isActive={true}
                                                                />
                                                                <button
                                                                    onClick={handleSeaman1Add}
                                                                    type="button"
                                                                    disabled={!selectedSeaman1Id || seamanAssignLoading}
                                                                    className="rounded-md bg-primary px-3 py-1.5 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {seamanAssignLoading ? t('Saving...') : t('Add')}
                                                                </button>
                                                                <button
                                                                    onClick={handleSeaman1Cancel}
                                                                    type="button"
                                                                    disabled={seamanAssignLoading}
                                                                    className="rounded-md bg-gray-400 px-3 py-1.5 text-[14px] text-white shadow-sm hover:bg-gray-500 disabled:opacity-50"
                                                                >
                                                                    {t('Cancel')}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </dd>
                                                </div>
                                            )}
                                            {/* Seaman 2 Section */}
                                            {(historyInfo?.status?.code === 'ACT' || historyInfo?.status?.code === 'SCH') && (
                                                <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        {t('Seaman 2')}
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {!isAddingSeaman2 ? (
                                                            <>
                                                                {historyInfo?.seaman2?.user_info?.given_name || ''}{' '}
                                                                {historyInfo?.seaman2?.user_info?.family_name || ''}
                                                                {!historyInfo?.seaman2?.id && (
                                                                    <span className="italic text-gray-500">
                                                                        {t('No Seaman 2 Assigned')}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    onClick={handleSeaman2Change}
                                                                    type="button"
                                                                    disabled={seamanAssignLoading}
                                                                    className="rounded-md bg-primary px-2 py-1 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2 disabled:opacity-50"
                                                                >
                                                                    {historyInfo?.seaman2?.id ? t('Change') : t('Add')}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <UserListDropdown
                                                                    userType="seaman"
                                                                    fields={{
                                                                        name: 'seaman2Id',
                                                                        id: 'seaman2Id',
                                                                        value: selectedSeaman2Id,
                                                                        onChange: e => setSelectedSeaman2Id(e.target.value),
                                                                    }}
                                                                    purpose="add"
                                                                    isActive={true}
                                                                />
                                                                <button
                                                                    onClick={handleSeaman2Add}
                                                                    type="button"
                                                                    disabled={!selectedSeaman2Id || seamanAssignLoading}
                                                                    className="rounded-md bg-primary px-3 py-1.5 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {seamanAssignLoading ? t('Saving...') : t('Add')}
                                                                </button>
                                                                <button
                                                                    onClick={handleSeaman2Cancel}
                                                                    type="button"
                                                                    disabled={seamanAssignLoading}
                                                                    className="rounded-md bg-gray-400 px-3 py-1.5 text-[14px] text-white shadow-sm hover:bg-gray-500 disabled:opacity-50"
                                                                >
                                                                    {t('Cancel')}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </dd>
                                                </div>
                                            )}
                                            {(historyInfo?.status?.code === 'ACT' ||
                                                historyInfo?.status?.code === 'SCH') && (
                                                <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                    <dt className="text-sm font-medium leading-6 text-gray-900">
                                                        Luggage Boat
                                                    </dt>
                                                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                        {historyInfo?.luggage_boats[0]?.name?.en ?? (
                                                            <span className="italic text-gray-500">
                                                                {t('No Luggage Boat Assigned')}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => {
                                                                handleLuggageBoatChange()
                                                            }}
                                                            type="button"
                                                            className="rounded-md bg-primary px-2 py-1 text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2"
                                                        >
                                                            {t('Change Luggage Boat')}
                                                        </button>
                                                    </dd>
                                                </div>
                                            )}
                                            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {t('Status')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {historyInfo?.status && (
                                                        <Badge
                                                            color={
                                                                historyInfo.status.code === 'SCH'
                                                                    ? 'orange'
                                                                    : historyInfo.status.code === 'INA'
                                                                    ? 'purple'
                                                                    : historyInfo.status.code === 'ACT'
                                                                    ? 'emerald'
                                                                    : 'zinc'
                                                            }
                                                            className="capitalize"
                                                        >
                                                            {historyInfo.status?.description?.en}
                                                        </Badge>
                                                    )}
                                                </dd>
                                            </div>
                                            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {t('Passenger Manifest')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    <div className="flex flex-col gap-3">
                                                        <div className="flex items-center gap-2">
                                                           
                                                            <select
                                                                value={passengerStatus}
                                                                onChange={(e) => setPassengerStatus(e.target.value)}
                                                                className="block w-48 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                            >
                                                                <option value="">{t('Select status')}</option>
                                                                <option value="BOOKED">{t('Booked')}</option>
                                                                <option value="BOARDED">{t('Boarded')}</option>
                                                            </select>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={handleSendPassengerManifest}
                                                                type="button"
                                                                disabled={emailLoading || !passengerStatus}
                                                                className={
                                                                    'rounded-md bg-primary px-3 py-2 text-[14px] text-white shadow-sm font-semibold hover:bg-primary/[0.9] ms-0 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1'
                                                                }
                                                            >
                                                                <EnvelopeIcon className="h-4 w-4" />
                                                                {emailLoading ? t('Sending...') : t('Email Passenger Manifest')}
                                                            </button>
                                                            <button
                                                                onClick={handleDownloadPassengerManifest}
                                                                type="button"
                                                                disabled={downloadManifestLoading || !passengerStatus}
                                                                className={
                                                                    'rounded-md bg-primary px-3 py-2 text-[14px] text-white shadow-sm font-semibold hover:bg-primary/[0.9] ms-0 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1'
                                                                }
                                                            >
                                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                                                {downloadManifestLoading ? t('Downloading...') : t('Passenger Manifest')}
                                                            </button>
                                                            <button
                                                                onClick={handleDownloadPassengerAttachments}
                                                                type="button"
                                                                disabled={downloadAttachmentsLoading || !passengerStatus}
                                                                className={
                                                                    'rounded-md bg-secondary px-3 py-2 text-[14px] text-white shadow-sm font-semibold hover:bg-secondary/[0.9] ms-0 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1'
                                                                }
                                                            >
                                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                                                {downloadAttachmentsLoading ? t('Downloading...') : t('Passengers Attachments')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </dd>
                                            </div>
                                            
                                        </dl>
                                    </div>
                                </div>
                                <div className="col-span-2 flex-1">
                                    <div className="shrink-0 rounded-lg xl:mb-0 p-6 bg-white border-solid border-2 border-gray-200  h-max w-full !pb-0 !pt-0">
                                        <div className="">
                                            <div className="flow-root mt-6 mb-6">
                                                <ul className="-mb-8">
                                                    {historyInfo?.waypoints.map((tripSchedule, eventIdx) => (
                                                        <li key={tripSchedule.stop_number}>
                                                            <div className="relative pb-4">
                                                                {eventIdx !== (historyInfo?.waypoints).length - 1 ? (
                                                                    <span
                                                                        aria-hidden="true"
                                                                        className="absolute left-2 top-4 ml-[69px] h-full w-0.5 bg-gray-200"
                                                                    />
                                                                ) : null}
                                                                <div className="relative flex space-x-3 ">
                                                                    <p className="mt-[-3px] text-[14px]">
                                                                        {formatTimeToAmPm(tripSchedule.departure_time)}
                                                                    </p>
                                                                    <div>
                                                                        <span
                                                                            className={classNames(
                                                                                tripSchedule.iconBackground,
                                                                                'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                            )}
                                                                        ></span>
                                                                    </div>
                                                                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-[1px] border-gray-100 pb-4 mt-[-5px]">
                                                                        <div>
                                                                            <p className="text-md text-gray-900 font-medium ">
                                                                                {tripSchedule.location?.name?.en}
                                                                            </p>

                                                                            <p className="flex text-sm text-gray-400 font-medium pt-2 items-center">
                                                                                <Badge
                                                                                    color={
                                                                                        tripSchedule.status?.code ===
                                                                                        'OPN'
                                                                                            ? 'emerald'
                                                                                            : 'rose'
                                                                                    }
                                                                                    className={'!rounded-full'}
                                                                                >
                                                                                    {tripSchedule.status?.code === 'OPN'
                                                                                        ? 'Open'
                                                                                        : 'Closed'}
                                                                                </Badge>
                                                                            </p>
                                                                        </div>
                                                                        <div className="whitespace-nowrap text-right text-sm text-gray-500 flex justify-end items-end">
                                                                            {historyInfo?.status?.code === 'OPN' ||
                                                                            historyInfo?.status?.code === 'SCH' ? (
                                                                                tripSchedule.status?.code === 'OPN' ? (
                                                                                    <button
                                                                                        className="text-white rounded-md bg-red px-2 py-1 text-[14px]shadow-sm ms-2"
                                                                                        onClick={() =>
                                                                                            handleWaypointStatus(
                                                                                                tripSchedule,
                                                                                                'close'
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Close Station
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        className='className="inline-flex items-center justify-center rounded-md bg-primary px-2 py-1 text-sm text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2"'
                                                                                        onClick={() =>
                                                                                            handleWaypointStatus(
                                                                                                tripSchedule,
                                                                                                'open'
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Open Station
                                                                                    </button>
                                                                                )
                                                                            ) : (
                                                                                ''
                                                                            )}
                                                                            {/* {tripSchedule.status?.code === 'OPN' && 
                                                                            (historyInfo?.status?.code === 'SCH' || historyInfo?.status?.code === 'ACT') ? (
                                                                                <button
                                                                                    className="text-white rounded-md bg-red px-2 py-1 text-[14px]shadow-sm ms-2"
                                                                                    onClick={() =>
                                                                                        handleWaypointStatus(
                                                                                            tripSchedule
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Close Station
                                                                                </button>
                                                                            ) : (
                                                                                <button
                                                                                    className='className="inline-flex items-center justify-center rounded-md bg-primary px-2 py-1 text-sm text-[14px] text-white shadow-sm hover:bg-primary/[0.9] ms-2"'
                                                                                    onClick={() =>
                                                                                        handleWaypointStatus(
                                                                                            tripSchedule
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Open Station
                                                                                </button>
                                                                            )} */}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Meal Summary Section */}
                            {!historyLoading && !waypointLoading && !historyListError && historyInfo && (
                                <div className="mt-8">
                                    <div className="border-2 border-red-500 rounded-lg p-6 bg-white">
                                        <h3 className="text-lg font-semibold text-red-600 mb-4 text-center">
                                            {t('Meal Summary Table')}
                                        </h3>

                                        {mealLoading && (
                                            <div className="flex flex-col justify-center items-center bg-white h-32">
                                                <img alt="logo" src={loadingImage} className="w-[100px]" />
                                                <p className="pt-3 text-primary font-semibold text-sm">
                                                    {t('Loading meals')}...
                                                </p>
                                            </div>
                                        )}

                                        {mealError && (
                                            <div className="flex justify-center items-center bg-white py-5">
                                                <div className="text-sm text-center text-red-600">
                                                    {t('Failed to fetch meal summary!')}
                                                </div>
                                            </div>
                                        )}

                                        {!mealLoading && !mealError && mealSummaryData?.length === 0 && (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                <div className="text-sm text-gray-500">
                                                    {t('No meals found for this trip')}
                                                </div>
                                            </div>
                                        )}

                                        {!mealLoading && !mealError && mealSummaryData?.length > 0 && (
                                            <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                                                <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeader>{t('English Description')}</TableHeader>
                                                            <TableHeader>{t('Arabic Description')}</TableHeader>
                                                            <TableHeader className="text-center">
                                                                {t('Meal Count')}
                                                            </TableHeader>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {mealSummaryData.map(meal => (
                                                            <TableRow key={meal.meal_id} className="hover:bg-gray-50">
                                                                <TableCell className="font-medium">
                                                                    {meal.meal_name_en}
                                                                </TableCell>
                                                                <TableCell>{meal.meal_name_ar}</TableCell>
                                                                <TableCell className="text-center">
                                                                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                                                                        {meal.meal_count}
                                                                    </span>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleSucClose} />
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={successMessage} onClose={handleSucClose} />
            )}
            {showBoatAssignDialog && (
                <AssignBoatDialog
                    status={true}
                    title={t('Change Boat')}
                    historyParams={selectedHistory}
                    onClose={handleCloseDialog}
                    onSuccess={() => {
                        console.log('BOAT==onSuccess')
                        setShowSucDialog(true)
                        setSuccessMessage('Boat assigned successfully')
                        searchAssignmentHistory()
                        setTimeout(() => {
                            setShowBoatAssignDialog(false)
                            handleSucClose()
                        }, 1500)
                    }}
                />
            )}
            {showBoatLuggageAssignDialog && (
                <AssignLuggageBoatDialog
                    status={true}
                    title={t('Change Luggage Boat')}
                    historyParams={selectedHistory}
                    onClose={handleCloseDialog}
                    onSuccess={() => {
                        console.log('BOAT_LUGGAGE==onSuccess')
                        setShowSucDialog(true)
                        setSuccessMessage('Luggage Boat assigned successfully')
                        searchAssignmentHistory()
                        setTimeout(() => {
                            setShowBoatLuggageAssignDialog(false)
                            handleSucClose()
                        }, 1500)
                    }}
                />
            )}
            {showCaptainAssignDialog && (
                <AssignCaptainDialog
                    status={true}
                    title={t('Change Captain')}
                    historyParams={selectedHistory}
                    onClose={handleCloseDialog}
                    onSuccess={() => {
                        console.log('CAP==onSuccess')
                        setShowSucDialog(true)
                        setSuccessMessage('Captain assigned successfully')
                        searchAssignmentHistory()
                        setTimeout(() => {
                            setShowBoatAssignDialog(false)
                            handleSucClose()
                        }, 1500)
                    }}
                />
            )}
            {showCaptain2AssignDialog && (
                <AssignAdditionalCaptainDialog
                    status={true}
                    title={t('Add Additional Captain')}
                    historyParams={selectedHistory}
                    onClose={handleCloseDialog}
                    onSuccess={() => {
                        console.log('CAP2==onSuccess')
                        setShowSucDialog(true)
                        setSuccessMessage('Additional Captain assigned successfully')
                        searchAssignmentHistory()
                        setTimeout(() => {
                            setShowCaptain2AssignDialog(false)
                            handleSucClose()
                        }, 1500)
                    }}
                />
            )}
            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={confirmDialogTitle}
                    negativeActionText="Cancel"
                    positiveActionText="Yes"
                    message={confirmDialogMessage}
                    onPositive={() => {
                        setShowConfirmDialog(false)
                        confirmDialogAction()
                    }}
                    onNegative={() => {
                        setShowConfirmDialog(false)
                    }}
                />
            )}
            {showSeatManagementModal && (
                <SeatManagementModal
                    open={showSeatManagementModal}
                    onClose={() => setShowSeatManagementModal(false)}
                    tripData={historyInfo}
                    onSuccess={() => {
                        searchAssignmentHistory()
                    }}
                />
            )}
        </>
    )
}
